import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { isDesktop, isMobile, isTablet } from 'react-device-detect'
import { When } from 'react-if'
import { Button as AtomButton, LinkButton } from '@/atoms/Button'
import { CircleAlertIcon } from '@/atoms/Icons/CircleAlertIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { GiftIcon } from '@/atoms/Icons/GiftIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { AsH1, LabelLG, TitleXS } from '@/atoms/Text'
import { Paragraph } from '@/atoms/Typography'
import { paths } from '@/constants'
import { projectTypes } from '@/constants/projectTypes'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { GuildSubscribeButton } from '@/molecules/GuildSubscribeButton'
import { PayItForwardButton } from '@/molecules/PayItForwardButton'
import { useToast } from '@/molecules/Toast'
import { Asset, useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { useCollectiblesPlayer } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerContext'
import { FavoriteASceneContainer } from '@/organisms/CollectiblesPlayer/FavoriteASceneContainer'
import { CatalogTitle, formatTitleLikeLandscapeImagePath, formatTitleMoviePosterPath } from '@/services/ContentCatalog'
import { createShareLink, extractLastGuid } from '@/services/ContentShareService'
import { useGuildUser } from '@/services/GuildUserService'
import { Episode, ProjectType } from '@/services/ProjectsService'
import { useUser } from '@/services/UserService'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { cLogger } from '@/utils/logging/client-logger'
import { getBasePath } from '@/utils/sitemap/base'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { getIsAngelEmployeeUser, getUserDisplayName } from '@/utils/users'
import { GuildContentLinkingModal, GuildContentLinkingSheet } from './GuildContentLinking'

interface CollectiblesPlayerInformationSectionProps {
  canShowFavoriteAScene?: boolean
  description: string
  handlePifButtonClick: () => void
  hasPayItForward: boolean
  name?: string
  projectSlug: string
  projectType?: ProjectType
  subtitle: string
  shouldShowGuildCTA?: boolean
  handleGuildButtonClick?: () => void
  catalogTitle?: CatalogTitle
  episodeId?: string
  asset?: Asset
  episode: Episode
  showShareModal: boolean
  setShowShareModal: (showShareModal: boolean) => void
}

export const CollectiblesPlayerInformationSection: React.FC<CollectiblesPlayerInformationSectionProps> = React.memo(
  ({
    canShowFavoriteAScene,
    description,
    handlePifButtonClick,
    hasPayItForward,
    name,
    projectSlug,
    projectType,
    subtitle,
    shouldShowGuildCTA,
    handleGuildButtonClick,
    catalogTitle,
    episodeId,
    asset,
    episode,
    showShareModal,
    setShowShareModal,
  }) => {
    const [shareUrl, setShareUrl] = useState('')
    const [text, setText] = useState('')
    const [recipient, setRecipient] = useState('')
    const [isCopied, setIsCopied] = useState(false)
    const [isCreateLoading, setIsCreateLoading] = useState(false)
    const { locale } = useLocale()
    const { t } = useTranslate('watch')
    const wasPlayingRef = useRef(false)
    const track = useSafeTrack()
    const { isGuildMember } = useGuildUser()
    const { user } = useUser()
    const isAngelEmployeeUser = getIsAngelEmployeeUser(user?.email)
    const baseUrl = getBasePath(locale)
    const toast = useToast()
    const displayName = getUserDisplayName(user)
    const searchParams = useSearchParams()
    const shareLinkCopied = t('shareLinkCopied', 'Share Link Copied.')
    const shareLinkError = t('shareLinkError', 'There was an error copying the link. Please try again.')
    const moviePosterPath = returnMoviePosterPath(catalogTitle)
    const movieMobilePosterPath = returnMobileMoviePosterPath(catalogTitle)
    const isEpisodeEarlyAccess = Boolean(episode?.prereleaseAvailableFor?.includes('GUILD'))

    const canShowExperiment = returnCanShowExperiment(isEpisodeEarlyAccess, isGuildMember)
    const { getCurrentMoment } = useCollectiblesPlayer()
    const { controller } = useAngelPlayerContext()

    const handleSayWhyYouLoveClick = useCallback(() => {
      const currentMoment = getCurrentMoment()
      if (!currentMoment || !controller) {
        return
      }

      if (controller.isPlaying()) {
        wasPlayingRef.current = controller.isPlaying()
        controller.pause()
      }

      track('Show Tell Why You Love Clicked', {
        momentId: currentMoment.id,
        playerTime: controller.getCurrentTime(),
        projectSlug: projectSlug,
        context: 'Reaction Button Prompt',
      })
    }, [track, controller, getCurrentMoment, projectSlug])

    const handleShareButtonClick = useCallback(() => {
      track('Share With Friend Button Clicked', {
        projectSlug,
        context: 'Player',
      })
      setShowShareModal(true)
    }, [track, projectSlug, setShowShareModal])

    const handleLinkCopied = useCallback(() => {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          toast?.showToast(shareLinkCopied, { icon: <CopyIcon size={24} color="gray-200" /> })
          setIsCopied(true)
        })
        .catch(() => {
          toast?.showToast(shareLinkError, { icon: <CircleAlertIcon size={24} color="gray-200" /> })
        })
    }, [shareLinkCopied, shareLinkError, shareUrl, toast])

    const createCopyLink = useCallback(() => {
      if (text?.length === 0 || recipient?.length === 0) {
        toast?.showToast(t('pleaseCompleteBothFields', 'Please complete both fields to share link'), {
          icon: <CircleAlertIcon size={24} color="gray-200" />,
        })
        return
      }
      setIsCreateLoading(true)
      createShareLink({
        variant: '1',
        sharer_id: user?.id,
        recipient_name: recipient,
        sender_note: text,
        slug: projectSlug,
        content_uuid: episodeId,
        m3u8_url: asset?.sourceConfig?.hls,
      })
        .then((response) => {
          const generatedUrl = response?.link
          track('Guild Content Share Link Created', {
            sharer_id: user?.id,
            slug: projectSlug,
            content_uuid: episodeId,
          })
          if (generatedUrl) {
            const lastGuid = extractLastGuid(generatedUrl)
            const shareUrl = `${baseUrl}/watch/share/${lastGuid}`
            setShareUrl(shareUrl)
            navigator.clipboard.writeText(shareUrl).then(() => {
              toast?.showToast(shareLinkCopied, { icon: <CopyIcon size={24} color="gray-200" /> })
            })
          }
          setIsCreateLoading(false)
        })
        .catch((error) => {
          setIsCreateLoading(false)
          cLogger().error('Error creating share link', error)
        })
    }, [
      text,
      recipient,
      user?.id,
      projectSlug,
      episodeId,
      asset?.sourceConfig?.hls,
      toast,
      t,
      track,
      baseUrl,
      shareLinkCopied,
    ])

    const handleNewShare = () => {
      setRecipient('')
      setText('')
      setShareUrl('')
      setIsCopied(false)
    }

    const handleModalClose = () => {
      setShowShareModal(false)
    }

    const Button = renderButton(
      Boolean(shouldShowGuildCTA),
      projectSlug,
      Boolean(isGuildMember),
      Boolean(hasPayItForward),
      handlePifButtonClick,
      t,
      projectType,
      handleGuildButtonClick,
    )

    useEffect(() => {
      searchParams.has('share') && setShowShareModal(true)
    }, [searchParams, setShowShareModal])

    return (
      <>
        <PaddedContainer fluid className="flex flex-col-reverse justify-between space-y-4 md:flex-row">
          <div className="flex w-full flex-col flex-wrap space-y-4 md:flex-nowrap">
            <div className="flex w-full flex-col-reverse justify-between space-y-8">
              <div className="flex items-center justify-between space-y-4">
                <header className="mt-10 flex flex-col space-y-8 md:mt-4">
                  <div>
                    <When condition={projectType !== projectTypes.movie}>
                      <Paragraph aria-label="player-episode-mapping" color="text-gray-500" className="mb-0.5">
                        {name}
                      </Paragraph>
                    </When>
                    <TitleXS weight="bold" as={AsH1} aria-label="player-episode-title" className="mb-1">
                      {subtitle}
                    </TitleXS>
                  </div>
                </header>
                <When condition={canShowFavoriteAScene}>
                  <div className="hidden md:flex">
                    <FavoriteASceneContainer onSayWhyYouLoveClick={handleSayWhyYouLoveClick} />
                  </div>
                </When>
                <When condition={canShowExperiment}>
                  <When condition={isAngelEmployeeUser}>
                    <div className="h-12">
                      <div className="hidden sm:flex md:mb-8 md:ml-auto md:hidden md:w-fit lg:flex">
                        <AtomButton
                          onClick={handleShareButtonClick}
                          variant="core-gray-800"
                          className="flex min-h-12 flex-row items-center justify-center gap-1 rounded-[50px] px-[18px] py-[7px]"
                        >
                          <GiftIcon size={24} className="-mt-0.5" />
                          <LabelLG className="mt-[1px] flex h-full items-center justify-center">
                            {t('shareWithAFriend', 'Share with a friend')}
                          </LabelLG>
                        </AtomButton>
                      </div>
                    </div>
                  </When>
                  <When condition={!isAngelEmployeeUser}>
                    <GBFadeInExperiment defaultValue={false} experimentName="guild_content_linking" className="h-12">
                      <ExperimentVariation variation={true}>
                        <div className="hidden sm:flex md:mb-8 md:ml-auto md:hidden md:w-fit lg:flex">
                          <AtomButton
                            onClick={handleShareButtonClick}
                            variant="core-gray-800"
                            className="flex min-h-12 flex-row items-center justify-center gap-1 rounded-[50px] px-[18px] py-[7px]"
                          >
                            <GiftIcon size={24} className="-mt-0.5" />
                            <LabelLG className="mt-[1px] flex h-full items-center justify-center">
                              {t('shareWithAFriend', 'Share with a friend')}
                            </LabelLG>
                          </AtomButton>
                        </div>
                      </ExperimentVariation>
                      <ExperimentVariation variation={false}>
                        <div className="hidden sm:flex md:mb-8 md:ml-auto md:hidden md:w-fit">{Button}</div>
                      </ExperimentVariation>
                      <ExperimentVariation default>
                        <div className="hidden sm:flex md:mb-8 md:ml-auto md:hidden md:w-fit">{Button}</div>
                      </ExperimentVariation>
                    </GBFadeInExperiment>
                  </When>
                </When>
                <When condition={!canShowExperiment}>
                  <div className="hidden sm:flex md:mb-8 md:ml-auto md:hidden md:w-fit">{Button}</div>
                </When>
              </div>
              <When condition={canShowExperiment}>
                <When condition={isAngelEmployeeUser}>
                  <div className="-mt-2 flex w-full justify-center sm:hidden md:ml-auto md:w-fit">
                    <AtomButton
                      onClick={handleShareButtonClick}
                      variant="core-gray-800"
                      className="flex min-h-12 flex-row items-center justify-center gap-1 rounded-[50px] px-[18px] py-[7px]"
                    >
                      <GiftIcon size={24} className="-mt-0.5" />
                      <LabelLG className="mt-[1px] flex h-full items-center justify-center">
                        {t('shareWithAFriend', 'Share with a friend')}
                      </LabelLG>
                    </AtomButton>
                  </div>
                </When>
                <When condition={!isAngelEmployeeUser}>
                  <GBFadeInExperiment defaultValue={false} experimentName="guild_content_linking">
                    <ExperimentVariation variation={true}>
                      <div className="-mt-2 flex w-full justify-center sm:hidden md:ml-auto md:w-fit">
                        <AtomButton
                          onClick={handleShareButtonClick}
                          variant="core-gray-800"
                          className="flex min-h-12 flex-row items-center justify-center gap-1 rounded-[50px] px-[18px] py-[7px]"
                        >
                          <GiftIcon size={24} className="-mt-0.5" />
                          <LabelLG className="mt-[1px] flex h-full items-center justify-center">
                            {t('shareWithAFriend', 'Share with a friend')}
                          </LabelLG>
                        </AtomButton>
                      </div>
                    </ExperimentVariation>
                    <ExperimentVariation variation={false}>
                      <When condition={hasPayItForward}>
                        <div className="w-full sm:hidden md:ml-auto md:w-fit">{Button}</div>
                      </When>
                    </ExperimentVariation>
                    <ExperimentVariation default>
                      <When condition={hasPayItForward}>
                        <div className="w-full sm:hidden md:ml-auto md:w-fit">{Button}</div>
                      </When>
                    </ExperimentVariation>
                  </GBFadeInExperiment>
                </When>
              </When>
              <When condition={!canShowExperiment}>
                <div className="w-full sm:hidden md:ml-auto md:w-fit">{Button}</div>
              </When>
              <When condition={canShowFavoriteAScene}>
                <div className="flex h-20 justify-end md:hidden">
                  <FavoriteASceneContainer onSayWhyYouLoveClick={handleSayWhyYouLoveClick} />
                </div>
              </When>
            </div>
            <div className="flex flex-col-reverse sm:flex-row">
              <Paragraph className="mr-8 max-w-[600px]" color="text-gray-300">
                {description}
              </Paragraph>
              <When condition={canShowExperiment}>
                <When condition={isAngelEmployeeUser}>
                  <div className="w-full">
                    <div className="hidden w-full justify-end md:ml-auto md:flex md:w-fit lg:hidden">
                      <AtomButton
                        onClick={handleShareButtonClick}
                        variant="core-gray-800"
                        className="flex min-h-12 min-w-[232px] flex-row items-center justify-center gap-1 rounded-[50px] px-[18px] py-[7px]"
                      >
                        <GiftIcon size={24} className="-mt-0.5" />
                        <LabelLG className="mt-[1px] flex h-full items-center justify-center">
                          {t('shareWithAFriend', 'Share with a friend')}
                        </LabelLG>
                      </AtomButton>
                    </div>
                  </div>
                </When>
                <When condition={!isAngelEmployeeUser}>
                  <GBFadeInExperiment defaultValue={false} experimentName="guild_content_linking" className="w-full">
                    <ExperimentVariation variation={true}>
                      <div className="hidden w-full justify-end md:ml-auto md:flex md:w-fit lg:hidden">
                        <AtomButton
                          onClick={handleShareButtonClick}
                          variant="core-gray-800"
                          className="flex min-h-12 min-w-[232px] flex-row items-center justify-center gap-1 rounded-[50px] px-[18px] py-[7px]"
                        >
                          <GiftIcon size={24} className="-mt-0.5" />
                          <LabelLG className="mt-[1px] flex h-full items-center justify-center">
                            {t('shareWithAFriend', 'Share with a friend')}
                          </LabelLG>
                        </AtomButton>
                      </div>
                    </ExperimentVariation>
                    <ExperimentVariation variation={false}>
                      <When condition={hasPayItForward}>
                        <div className="hidden w-full md:ml-auto md:flex md:w-fit">{Button}</div>
                      </When>
                    </ExperimentVariation>
                    <ExperimentVariation default>
                      <When condition={hasPayItForward}>
                        <div className="hidden w-full md:ml-auto md:flex md:w-fit">{Button}</div>
                      </When>
                    </ExperimentVariation>
                  </GBFadeInExperiment>
                </When>
              </When>
            </div>
          </div>
        </PaddedContainer>
        <When condition={isMobile}>
          <GuildContentLinkingSheet
            showModal={showShareModal}
            handleModalClose={handleModalClose}
            createCopyLink={createCopyLink}
            moviePosterPath={movieMobilePosterPath}
            catalogTitle={catalogTitle}
            displayName={displayName}
            recipient={recipient}
            text={text}
            setRecipient={setRecipient}
            setText={setText}
            shareUrl={shareUrl}
            handleLinkCopied={handleLinkCopied}
            isCopied={isCopied}
            handleNewShare={handleNewShare}
            isCreateLoading={isCreateLoading}
          />
        </When>
        <When condition={isTablet || isDesktop}>
          <GuildContentLinkingModal
            showModal={showShareModal}
            handleModalClose={handleModalClose}
            createCopyLink={createCopyLink}
            moviePosterPath={moviePosterPath}
            catalogTitle={catalogTitle}
            displayName={displayName}
            recipient={recipient}
            text={text}
            setRecipient={setRecipient}
            setText={setText}
            shareUrl={shareUrl}
            handleLinkCopied={handleLinkCopied}
            isCopied={isCopied}
            handleNewShare={handleNewShare}
            isCreateLoading={isCreateLoading}
          />
        </When>
      </>
    )
  },
)

CollectiblesPlayerInformationSection.displayName = 'React.memo(CollectiblesPlayerInformationSection)'

const returnMoviePosterPath = (catalogTitle?: CatalogTitle): string | null | undefined => {
  return catalogTitle ? formatTitleMoviePosterPath(catalogTitle) : null
}

const returnMobileMoviePosterPath = (catalogTitle?: CatalogTitle): string | null | undefined => {
  return catalogTitle ? formatTitleLikeLandscapeImagePath(catalogTitle) : null
}

const returnCanShowExperiment = (isProjectInEarlyAccess: boolean, isGuildMember: boolean): boolean => {
  return isProjectInEarlyAccess && isGuildMember
}

const renderButton = (
  shouldShowGuildCTA: boolean,
  projectSlug: string,
  isGuildMember: boolean,
  hasPayItForward: boolean,
  handlePifButtonClick: () => void,
  t: TranslateFunction,
  projectType?: string,
  handleGuildButtonClick?: () => void,
) => {
  if (shouldShowGuildCTA) {
    return (
      <GuildSubscribeButton
        className="max-h-12 w-full whitespace-nowrap"
        projectSlug={projectSlug}
        projectType={projectType}
        onClick={handleGuildButtonClick}
      />
    )
  }

  if (!isGuildMember) {
    return (
      <LinkButton
        href={`${paths.guild.join}/${projectSlug}`}
        variant={'oxide'}
        className={'max-h-12 w-full whitespace-nowrap px-10 py-3'}
      >
        <div className="relative flex flex-row items-center justify-center gap-2">
          <GuildIconMono color="white" size={20} className="mr-1" />
          {t('joinTheGuild', 'Join The Guild')}
        </div>
      </LinkButton>
    )
  }

  return (
    <PayItForwardButton
      className="max-h-12 w-full whitespace-nowrap"
      pifEnabled={hasPayItForward}
      projectSlug={projectSlug}
      onClick={handlePifButtonClick}
      utmSource="below-video-player-pif-button"
    />
  )
}
